import type {
  CodeableConcept,
  Extension,
  Invoice,
  ValueSetExpansionContains,
} from "@medplum/fhirtypes";

import { Extensions } from "@/fhir/enums/extensions";

/**
 * Child `url`s inside the facility admission extension.
 *
 * Bare names rather than full URLs: only the parent carries the canonical URL, which is how FHIR
 * shapes a complex extension and what `qh-facility-admission.json` slices on.
 */
export const FACILITY_ADMISSION_PART = {
  AdmissionDateTime: "admissionDateTime",
  AdmissionType: "admissionType",
  AdmissionSource: "admissionSource",
  ClientStatus: "clientStatus",
} as const;

/**
 * Facility admission as the drawer and the statement blocks deal with it.
 *
 * `admissionDateTime` is one UTC instant, not the drawer's separate date and time fields: the two
 * inputs are combined in the practice timezone before they get here, so what is stored is unambiguous
 * no matter where the biller is sitting.
 */
export interface FacilityAdmission {
  admissionDateTime: string;
  admissionType: CodeableConcept;
  admissionSource: CodeableConcept;
  clientStatus: CodeableConcept;
}

/**
 * The `CodeableConcept` a picker's expansion entry stores as.
 *
 * `ValueSetSelect` keeps the whole entry rather than just its code, so system and display travel
 * with it and the statement can print the display without re-expanding the value set.
 */
export function toCodeableConcept(entry: ValueSetExpansionContains): CodeableConcept {
  return {
    coding: [{ system: entry.system, code: entry.code, display: entry.display }],
  };
}

/**
 * The expansion entry a stored concept came from, for repopulating a picker.
 *
 * Only the first coding is read: the pickers write exactly one, and a concept carrying more did not
 * come from this drawer.
 */
export function toValueSetEntry(
  concept: CodeableConcept | undefined,
): ValueSetExpansionContains | undefined {
  const coding = concept?.coding?.[0];
  if (!coding) return undefined;

  return { system: coding.system, code: coding.code, display: coding.display };
}

/**
 * Reads the facility admission block off a statement.
 *
 * Every field is required together — the drawer will not save a partial one — so a block missing any
 * of them reads as absent rather than half-filled. That keeps the statement showing the empty-value
 * marker instead of a row or two of detail that was never really entered.
 */
export function getInvoiceFacilityAdmission(invoice: Invoice): FacilityAdmission | undefined {
  const parent = invoice.extension?.find(
    (extension) => extension.url === Extensions.FacilityAdmission,
  );
  if (!parent?.extension) return undefined;

  const part = (url: string) => parent.extension?.find((entry) => entry.url === url);

  const admissionDateTime = part(FACILITY_ADMISSION_PART.AdmissionDateTime)?.valueDateTime;
  const admissionType = part(FACILITY_ADMISSION_PART.AdmissionType)?.valueCodeableConcept;
  const admissionSource = part(FACILITY_ADMISSION_PART.AdmissionSource)?.valueCodeableConcept;
  const clientStatus = part(FACILITY_ADMISSION_PART.ClientStatus)?.valueCodeableConcept;

  if (!admissionDateTime || !admissionType || !admissionSource || !clientStatus) return undefined;

  return { admissionDateTime, admissionType, admissionSource, clientStatus };
}

/**
 * The single shape the facility admission extension takes on an Invoice. Shared so the reader above
 * and every writer go through here rather than re-deriving the child urls and value types.
 */
function facilityAdmissionExtension(admission: FacilityAdmission): Extension {
  return {
    url: Extensions.FacilityAdmission,
    extension: [
      {
        url: FACILITY_ADMISSION_PART.AdmissionDateTime,
        valueDateTime: admission.admissionDateTime,
      },
      { url: FACILITY_ADMISSION_PART.AdmissionType, valueCodeableConcept: admission.admissionType },
      {
        url: FACILITY_ADMISSION_PART.AdmissionSource,
        valueCodeableConcept: admission.admissionSource,
      },
      { url: FACILITY_ADMISSION_PART.ClientStatus, valueCodeableConcept: admission.clientStatus },
    ],
  };
}

/**
 * The statement's extensions with its facility admission block set or cleared.
 *
 * Filter-then-append rather than an in-place edit, so the balance-due and closure-date extensions
 * beside it survive untouched. An empty result collapses to `undefined` rather than an empty array,
 * which is what FHIR expects of an absent element.
 */
function withFacilityAdmission(
  invoice: Invoice,
  admission: FacilityAdmission | undefined,
): Extension[] | undefined {
  const preservedExtensions =
    invoice.extension?.filter((extension) => extension.url !== Extensions.FacilityAdmission) ?? [];

  if (!admission) {
    return preservedExtensions.length > 0 ? preservedExtensions : undefined;
  }

  return [...preservedExtensions, facilityAdmissionExtension(admission)];
}

/** A statement copy carrying the given facility admission block. */
export function applyFacilityAdmission(
  invoice: Invoice,
  admission: FacilityAdmission | undefined,
): Invoice {
  return { ...invoice, extension: withFacilityAdmission(invoice, admission) };
}
